import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useOptimizedUserRole } from './useOptimizedUserRole';
import { ensureLawyerProfile } from '@/utils/ensureLawyerProfile';
import { toast } from '@/hooks/use-toast';

interface AttorneySubscriptionData {
  is_trial: boolean; // DEPRECATED — always false; retained for type-compat with legacy call sites
  plan_kind?: 'free' | 'paid';
  subscription_tier: string;
  base_subscription_tier?: string;
  subscription_status: string;
  subscription_end_date?: string | null;
  days_remaining?: number;
  is_expired?: boolean;
  client_limit?: number;
  attorney_limit?: number;
  current_client_count?: number;
  cocounsel_enabled?: boolean;
  // Firm subscription info for team members
  is_firm_member?: boolean;
  firm_name?: string;
  firm_subscription_tier?: string;
  firm_subscription_status?: string;
  // Free tier caps (sourced from public.subscription_tiers WHERE tier_key='lawyer_free')
  tier_key?: string;
  case_limit?: number;
  matter_limit?: number;
  monthly_connection_limit?: number;
  messaging_scope?: 'connected_only' | 'unlimited' | string;
  time_tracking_enabled?: boolean;
  invoicing_enabled?: boolean;
  ai_questions_per_month?: number;
  tier_features?: string[];
}

// ─── Module-level deduplication cache ───
// Prevents 6+ identical edge-function calls when multiple components
// mount useAttorneySubscription() simultaneously.
interface CachedResult {
  data: AttorneySubscriptionData;
  timestamp: number;
}

const subscriptionCache = new Map<string, CachedResult>();
const inflightRequests = new Map<string, Promise<AttorneySubscriptionData | null>>();
const CACHE_TTL_MS = 30_000; // 30 seconds

// Global listener set so force-refreshes propagate to ALL hook instances
type Listener = (data: AttorneySubscriptionData) => void;
const globalListeners = new Set<Listener>();

const getCached = (userId: string): AttorneySubscriptionData | null => {
  const cached = subscriptionCache.get(userId);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }
  return null;
};

/** Clear the subscription cache for a user (e.g. after upgrade). */
export const clearAttorneySubscriptionCache = (userId?: string) => {
  if (userId) {
    subscriptionCache.delete(userId);
  } else {
    subscriptionCache.clear();
  }
};

export const useAttorneySubscription = () => {
  const { user } = useAuth();
  const userRoleHook = useOptimizedUserRole();
  const { isAttorney, lawyerData, lawFirmData, loading: roleLoading } = userRoleHook;
  const [subscriptionData, setSubscriptionData] = useState<AttorneySubscriptionData>({
    is_trial: false,
    plan_kind: 'free',
    subscription_tier: 'Free',
    subscription_status: 'active',
  });
  const [loading, setLoading] = useState(false);
  const [hasCheckedSubscription, setHasCheckedSubscription] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Track if we've already attempted a repair to prevent infinite loops
  const repairAttemptedRef = useRef(false);

  // Subscribe to global broadcast so force-refresh in one instance
  // updates ALL mounted instances (e.g. AttorneySubscriptionManager → useAttorneyAccountTier)
  useEffect(() => {
    const listener: Listener = (data) => {
      setSubscriptionData(data);
      setHasCheckedSubscription(true);
    };
    globalListeners.add(listener);
    return () => { globalListeners.delete(listener); };
  }, []);

  const checkSubscription = useCallback(async (forceRefresh = false, isRetryAfterRepair = false) => {
    // Wait for role resolution so free-profile attorneys don't briefly inherit trial access.
    if (!user || roleLoading || !isAttorney) {
      return;
    }

    // Check module-level cache first (unless force refresh)
    if (!forceRefresh && !isRetryAfterRepair) {
      const cached = getCached(user.id);
      if (cached) {
        setSubscriptionData(cached);
        setHasCheckedSubscription(true);
        setLoading(false);
        return;
      }
    }

    // Deduplicate in-flight requests — if another hook instance is already
    // fetching for this user, piggyback on the same promise.
    if (!forceRefresh && inflightRequests.has(user.id)) {
      try {
        const result = await inflightRequests.get(user.id);
        if (result) {
          setSubscriptionData(result);
          setHasCheckedSubscription(true);
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    // Create a deduplication-safe fetch promise
    const fetchPromise = (async (): Promise<AttorneySubscriptionData | null> => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setError('Please log in to check subscription status.');
        return null;
      }
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Function call timed out after 30 seconds')), 30000)
      );
      
      const functionCall = supabase.functions.invoke('attorney-subscription-v2', {
        body: {
          action: 'check_status',
          forceRefresh
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      
      const { data, error: funcError } = await Promise.race([functionCall, timeoutPromise]) as any;

      if (funcError) {
        const errorMessage = funcError.message || 'Failed to check subscription status';
        console.error('Error checking attorney subscription:', funcError);
        
        if (errorMessage.includes('Lawyer profile not found') && !isRetryAfterRepair && !repairAttemptedRef.current) {
          console.log('🔧 Detected missing lawyer profile - attempting self-healing repair...');
          repairAttemptedRef.current = true;
          
          try {
            const repairResult = await ensureLawyerProfile();
            
            if (repairResult.success && repairResult.lawyer) {
              console.log('✅ Successfully repaired lawyer profile, retrying subscription check...');
              toast({
                title: 'Profile Repaired',
                description: 'Your attorney profile has been set up. Loading subscription data...',
              });
              
              setLoading(false);
              await checkSubscription(true, true);
              return null;
            } else {
              console.error('❌ Failed to repair lawyer profile:', repairResult.error);
              toast({
                title: 'Profile Setup Required',
                description: repairResult.error || 'Please complete your attorney registration.',
                variant: 'destructive',
              });
            }
          } catch (repairError) {
            console.error('❌ Error during profile repair:', repairError);
          }
        }
        
        setError(`Edge function error: ${errorMessage}`);
        return null;
      }
      
      repairAttemptedRef.current = false;
      
      return data || {
        is_trial: false,
        plan_kind: 'free',
        subscription_tier: 'Free',
        subscription_status: 'active',
      };
    })();

    // Register inflight promise so other hook instances can piggyback
    if (user) {
      if (forceRefresh) {
        subscriptionCache.delete(user.id);
      }
      inflightRequests.set(user.id, fetchPromise);
    }

    try {
      setLoading(true);
      setError(null);

      const responseData = await fetchPromise;

      if (responseData) {
        // Cache the result
        if (user) {
          subscriptionCache.set(user.id, { data: responseData, timestamp: Date.now() });
        }
        setSubscriptionData(responseData);
        // Broadcast to all other hook instances so they update too
        if (forceRefresh) {
          globalListeners.forEach(fn => fn(responseData));
        }
      }
      setHasCheckedSubscription(true);
      
    } catch (error) {
      console.error('Network error checking attorney subscription:', error);
      setError(`Network error: ${error instanceof Error ? error.message : 'Failed to check subscription status'}`);
      setHasCheckedSubscription(true);
    } finally {
      if (user) {
        inflightRequests.delete(user.id);
      }
      setLoading(false);
    }
  }, [user?.id, isAttorney, lawFirmData?.id, roleLoading]);

  const createCheckout = useCallback(async (tier: any) => {
    if (!user || !isAttorney) {
      throw new Error('User must be an authenticated attorney');
    }

    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('Please log in to create a checkout session.');
    }

    const { data, error } = await supabase.functions.invoke('attorney-subscription-v2', {
      body: {
        action: 'create_subscription',
        tierName: tier.name,
        tierData: tier
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });

    if (error) {
      throw new Error(error.message || 'Failed to create checkout session');
    }

    return data;
  }, [user, isAttorney]);

  const openCustomerPortal = useCallback(async () => {
    if (!user || !isAttorney) {
      throw new Error('User must be an authenticated attorney');
    }

    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('Please log in to open the customer portal.');
    }

    const { data, error } = await supabase.functions.invoke('attorney-subscription-v2', {
      body: {
        action: 'customer_portal'
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });

    if (error) {
      throw new Error(error.message || 'Failed to open customer portal');
    }

    return data;
  }, [user, isAttorney]);

  // Check subscription on mount and when user/role changes
  useEffect(() => {
    if (!user) {
      setLoading(false);
      setHasCheckedSubscription(false);
      return;
    }

    if (roleLoading) {
      return;
    }

    if (!isAttorney) {
      setLoading(false);
      setHasCheckedSubscription(false);
      return;
    }

    setLoading(true);
    setHasCheckedSubscription(false);

    const timer = setTimeout(() => {
      checkSubscription(false);
    }, 100);

    return () => clearTimeout(timer);
  }, [user?.id, isAttorney, roleLoading, checkSubscription]);

  const effectiveLoading = roleLoading
    ? !!user
    : isAttorney
      ? loading || !hasCheckedSubscription
      : false;

  return {
    subscriptionData,
    loading: effectiveLoading,
    error,
    checkSubscription,
    createCheckout,
    openCustomerPortal,
    isAttorney,
    lawyerData
  };
};
